import Link from 'next/link';
import { useSearchParams } from 'next/navigation';
import { Dispatch, FocusEvent, ReactNode, SetStateAction, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';
import { palette } from 'styled-tools';

import { Button, ButtonSizes, FormInput, minWidth, Modal, Notice, TextButton } from '@calm-web/design-system';

import { useAnalytics } from '@/hooks/analytics';
import { LegacyCalmError } from '@/hooks/api/types';
import { GiftHook } from '@/hooks/purchase';
import { useUserState } from '@/hooks/store';
import { useComponentDidMount } from '@/hooks/utils/useComponentDidMount';
import { Subscription } from '@/store/user/types';
import { formatError } from '@/utils/formatErrors';
import getParamAsString from '@/utils/getParamsAsString';

import Image from '../image/NextImage';

const printedCard = '/_n/images/gift-card/asset-printed-card-example.webp';
const plasticCard = '/_n/images/gift-card/asset-plastic-card-example.webp';

const Wrapper = styled.div`
	box-sizing: border-box;
	padding: 0 1.66rem;
	color: ${palette('gray7')};
	width: 100%;

	${minWidth('mobileLarge')} {
		width: auto;
	}

	${minWidth('desktop')} {
		padding: 0;
		width: 100%;
	}
`;

const Title = styled.h4`
	width: 100%;
	margin-bottom: 1.5rem;
	text-align: center;

	${minWidth('desktop')} {
		text-align: left;
	}
`;

const CodeInput = styled(FormInput)`
	border-radius: 8px;
	margin-bottom: 1.5rem;
`;

const Divider = styled.div`
	width: 100%;
	border-bottom: 1px solid ${palette('gray3')};
	margin: 2rem 0;
`;

const CodeHelp = styled.div`
	margin-bottom: 24px;
	width: 100%;
	text-align: center;

	${minWidth('desktop')} {
		text-align: left;
	}
`;

const HelpLinkExternal = styled.a.attrs({
	target: '_blank',
	rel: 'noopener',
})`
	font-weight: 900;
	text-decoration: underline;
	cursor: pointer;
`;

const HelpLinkInternal = styled(Link)`
	font-weight: 900;
	text-decoration: underline;
	cursor: pointer;
`;

const ModalContents = styled.div`
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	justify-content: center;
`;

const Column = styled.div`
	width: 100%;
	box-sizing: border-box;
	padding: 10px;
	color: ${palette('gray7')};

	${minWidth('mobileLarge')} {
		width: 50%;
	}
`;

const HelpTitle = styled.div`
	font-weight: 900;
	margin-bottom: 0.75rem;
	width: 100%;
`;

const HelpDescription = styled.div`
	width: 100%;
`;

const HelpImage = styled.div`
	aspect-ratio: 1.31;
	position: relative;
	width: 100%;
	height: auto;
`;

const HelpButtonWrapper = styled.div`
	margin-top: 1rem;
	width: 144px;
`;

const TermsMessage = styled.div`
	line-height: 1.5;
	margin-top: 1.5rem;
	text-align: center;

	${minWidth('desktop')} {
		text-align: left;
	}
`;

const messages = defineMessages({
	timeoutError: {
		id: 'redeemFlow.redeemForm.errors.timeout',
		defaultMessage: 'Please try again when you have faster internet',
		description: 'An error we show when the request timed out',
	},
	genericError: {
		id: 'redeemFlow.redeemForm.errors.genericError',
		defaultMessage: 'There was an error redeeming your gift',
		description: 'An error we show when something went wrong but we do not know what',
	},
	alreadySubscribedError: {
		id: 'redeemFlow.redeemForm.errors.alreadySubscribedError',
		defaultMessage: 'You already have an active subscription on your account.',
		description: 'The user cannot redeem a gift card if they already have an active subscription',
	},
	giftAlreadyRedeemedError: {
		id: 'redeemFlow.redeemForm.errors.giftAlreadyRedeemedError',
		defaultMessage: 'This code has already been redeemed.',
		description: 'Gift cards can only be redeemed once, and this one has already been used',
	},
	invalidCodeError: {
		id: 'redeemFlow.redeemForm.errors.invalidCodeError',
		defaultMessage:
			'The code you entered is not a valid redemption code in our system. Please check for typos and try again.',
		description: 'We could not find the gift card code they typed in. They likely had a typo.',
	},
	loginRedeemError: {
		id: 'models.gift.loginRedeemError',
		defaultMessage: 'You must be logged in to redeem',
		description: 'They must be logged in to redeem a gift card code',
	},
	redeem: {
		id: 'redeemFlow.redeemForm.button.redeem',
		defaultMessage: 'Redeem',
		description: 'The text on the button for when they submit their gift card code for redemption',
	},
	success: {
		id: 'redeemFlow.redeemForm.success',
		defaultMessage: 'Success',
		description: 'The message we show when they successfully redeemed their gift card',
	},
	enterGiftCode: {
		id: 'redeemFlow.redeemForm.enterGiftCode',
		defaultMessage: 'Gift Code',
		description: 'The label for the form field where they enter their gift card code',
	},
	enterGiftPin: {
		id: 'redeemFlow.redeemForm.enterGiftPin',
		defaultMessage: 'Pin',
		description: 'The label for the form field where they enter their gift card pin',
	},
	redeemFormTitle: {
		id: 'redeem.redeemForm.title',
		defaultMessage: 'Enter your gift code',
		description: 'The title for the form where they enter their gift card code',
	},
	redeemTerms: {
		id: 'redeem.redeemForm.terms2',
		defaultMessage:
			'By clicking "Redeem", you agree to the <termslink>Gift Card Terms and Conditions</termslink>.',
		description: 'Entering a gift card code is subject to terms and conditions',
	},
	redeemLetExpire: {
		id: 'redeem.redeemform.help.redeemLetExpire',
		defaultMessage:
			'If you already have a Calm Premium subscription, please wait until it expires to redeem your Calm Gift Card.',
		description: 'A gift card cannot be redeemed while you already have a subscription.',
	},
	redeemHelp: {
		id: 'redeem.redeemForm.help',
		defaultMessage: "Can't find your code? <helplink>Look here</helplink>",
		description: 'A button for them to click when they cannot find their gift card code',
	},
	lifetimeError: {
		id: 'redeem.redeemForm.lifetimeError',
		defaultMessage:
			'If you receive an error message on this page, please <helplink>click here for further assistance</helplink>.',
		description: 'A link to help out erroring redeemers',
	},
	findGiftCode: {
		id: 'redeemFlow.redeemForm.findGiftCode',
		defaultMessage: 'Find your gift code',
		description: 'The title of a section where we help them find their gift card code',
	},
	printedCardHelpTitle: {
		id: 'redeem.redeemForm.help.printedCard.title',
		defaultMessage: 'Printed Gift Card',
		description:
			'There are two different kinds of gift cards, printed (on paper or PDF) and plastic. This is the label for the printed kind',
	},
	printedCardHelpDescription: {
		id: 'redeem.redeemForm.help.printedCard.description',
		defaultMessage: 'Six characters formatted as like {characters} at the bottom of the card.',
		description:
			'There are two different kinds of gift cards, printed (on paper or PDF) and plastic. This is the description of where to find the gift card code for the printed kind',
	},
	plasticCardHelpTitle: {
		id: 'redeem.redeemForm.help.plasticCard.title',
		defaultMessage: 'Plastic Gift Card',
		description:
			'There are two different kinds of gift cards, printed (on paper or PDF) and plastic. This is the label for the plastic kind',
	},
	plasticCardHelpDescription: {
		id: 'redeem.redeemForm.help.plasticCard.description',
		defaultMessage: 'A long set of characters like {characters}',
		description:
			'There are two different kinds of gift cards, printed (on paper or PDF) and plastic. This is the description of where to find the gift card code for the plastic kind',
	},
	helpSuccess: {
		id: 'redeem.redeemForm.help.success',
		defaultMessage: 'Got it',
		description: 'This is the button they click to close our helpful explainer',
	},
});

interface RedeemFormProps {
	gift: GiftHook;
	setIsLifetime: Dispatch<SetStateAction<boolean>>;
}

const RedeemForm = ({ gift, setIsLifetime }: RedeemFormProps) => {
	const searchParams = useSearchParams();
	const code = getParamAsString(searchParams?.get('code'));

	const [giftCode, setGiftCode] = useState(code ?? '');
	const [giftPin, setGiftPin] = useState('');
	const [errorCopy, setErrorCopy] = useState('');
	const [helpIsOpen, setHelpIsOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const user = useUserState();
	const { logEvent } = useAnalytics();
	const { formatMessage } = useIntl();

	useComponentDidMount(() => {
		logEvent({ eventName: 'Gift Redeem : Form : Landed' });
	});

	const validateForm = () => {
		const validGiftCode = Boolean(giftCode?.match(/^([A-Za-z0-9 ]){2,100}/));
		if (giftCode.length < 16) {
			return validGiftCode;
		}

		const validPin = Boolean(giftPin?.match(/^([A-Za-z0-9 ]){2,100}/));
		return validGiftCode && validPin;
	};
	const onBlurInput = (e: FocusEvent<HTMLInputElement>) => {
		const inputType = e.target.name === 'code' ? 'Code' : 'Pin';
		const isValid = validateForm();
		logEvent({
			eventName: `Gift Redeem : Form : ${inputType} : Entered`,
			eventProps: {
				is_valid: isValid,
				code: giftCode || null,
				pin: giftPin || null,
			},
		});
	};

	const buttonContent = () => {
		return gift.hasRedeemed ? formatMessage(messages.success) : formatMessage(messages.redeem);
	};

	const copyForError = (err: LegacyCalmError) => {
		if (err.didTimeout) {
			return formatMessage(messages.timeoutError);
		}
		switch (err.calmCode ?? err?.data?.error?.code) {
			case 'already_subscribed':
				return formatMessage(messages.alreadySubscribedError);
			case 'gift_already_redeemed':
				return formatMessage(messages.giftAlreadyRedeemedError);
			case 'invalid_code':
				return formatMessage(messages.invalidCodeError);
			case 'no_session':
				return formatMessage(messages.loginRedeemError);
			default:
				return formatMessage(messages.genericError);
		}
	};

	const errorNotice = () => {
		if (!errorCopy) {
			return undefined;
		}
		return (
			<div style={{ marginTop: '1em ' }}>
				<Notice
					isSuccess={false}
					onClick={() => {
						setErrorCopy('');
					}}
					describedBy="redeem-gift-card-form"
				>
					{errorCopy}
				</Notice>
			</div>
		);
	};

	const validateThenSubmit = async (
		e: React.FormEvent<HTMLFormElement> | React.MouseEvent<HTMLButtonElement>,
	) => {
		e.stopPropagation();
		e.preventDefault();
		if (!giftCode) {
			setErrorCopy(formatMessage(messages.invalidCodeError));
			return;
		}
		if (!validateForm()) {
			return;
		}
		logEvent({
			eventName: 'Gift Redeem : Form : Posted',
			eventProps: {
				code: giftCode || null,
			},
		});
		setErrorCopy('');
		setIsLoading(true);
		const response = await gift.redeemGift(user, giftCode, giftPin);
		setIsLoading(false);
		if (response?.error) {
			const newErrorCopy = copyForError(response.error as LegacyCalmError);
			logEvent({
				eventName: 'Gift Redeem : Form : Error',
				eventProps: {
					error_copy: newErrorCopy,
					...formatError(response.error as LegacyCalmError),
				},
			});
			setErrorCopy(newErrorCopy);
			return;
		}
		setIsLifetime((response?.success as { subscription: Subscription })?.subscription?.is_lifetime);
		logEvent({ eventName: 'Gift Redeem : Form : Success' });
	};

	const toggleHelp = () => {
		setHelpIsOpen(!helpIsOpen);
		// TODO: Log to analytics that they requested help
	};

	const renderHelpModal = () => (
		<Modal isOpen={helpIsOpen} closeModal={toggleHelp} canClose title={formatMessage(messages.findGiftCode)}>
			<ModalContents>
				<Column>
					<HelpImage>
						<Image fill src={printedCard} alt="a photo of a printed gift card" />
					</HelpImage>
				</Column>
				<Column>
					<HelpTitle>{formatMessage(messages.printedCardHelpTitle)}</HelpTitle>
					<HelpDescription>
						{formatMessage(messages.printedCardHelpDescription, {
							characters: <b>ABCDEF</b>, // eslint-disable-line @calm/react-intl/missing-formatted-message
						})}
					</HelpDescription>
				</Column>
				<Column>
					<HelpImage>
						<Image src={plasticCard} alt="a photo of a plastic gift card" fill />
					</HelpImage>
				</Column>
				<Column>
					<HelpTitle>{formatMessage(messages.plasticCardHelpTitle)}</HelpTitle>
					<HelpDescription>
						{formatMessage(messages.plasticCardHelpDescription, {
							characters: <b>XAFJ9ADFAD9JFA9</b>, // eslint-disable-line @calm/react-intl/missing-formatted-message
						})}
					</HelpDescription>
				</Column>
				<HelpButtonWrapper>
					<Button fullWidth backgroundColor="buttonLime" focusRingVariant="dark" onPress={toggleHelp}>
						{formatMessage(messages.helpSuccess)}
					</Button>
				</HelpButtonWrapper>
			</ModalContents>
		</Modal>
	);

	return (
		<Wrapper>
			<Title>{formatMessage(messages.redeemFormTitle)}</Title>
			<form onSubmit={validateThenSubmit} id="redeem-gift-card-form">
				<CodeInput
					name="code"
					value={giftCode ?? ''}
					onChange={e => {
						setGiftCode(e.currentTarget.value);
					}}
					label={formatMessage(messages.enterGiftCode)}
					onBlur={onBlurInput}
					noValidation
				/>
				{giftCode.length > 15 && (
					<CodeInput
						name="pin"
						value={giftPin}
						onChange={e => {
							setGiftPin(e.currentTarget.value);
						}}
						label={formatMessage(messages.enterGiftPin)}
						onBlur={onBlurInput}
						noValidation
					/>
				)}
				<Button
					legacyOnClick={validateThenSubmit}
					isDisabled={!validateForm()}
					isLoading={isLoading}
					textColor="white"
					backgroundColor="gradientRainbow6"
					size={ButtonSizes.Lg}
					fullWidth
					focusRingVariant="dark"
				>
					{buttonContent()}
				</Button>
			</form>
			{errorNotice()}
			<TermsMessage>
				{formatMessage(messages.redeemTerms, {
					termslink: (chunks: ReactNode) => <HelpLinkInternal href="/gift-terms">{chunks}</HelpLinkInternal>,
				})}
			</TermsMessage>
			<TermsMessage>{formatMessage(messages.redeemLetExpire)}</TermsMessage>
			<Divider />
			<CodeHelp>
				{formatMessage(messages.lifetimeError, {
					helplink: (chunks: ReactNode) => (
						<HelpLinkExternal href="https://support.calm.com/hc/en-us/articles/1260800773789">
							{chunks}
						</HelpLinkExternal>
					),
				})}
			</CodeHelp>
			<CodeHelp>
				{formatMessage(messages.redeemHelp, {
					helplink: (chunks: ReactNode) => <TextButton onClick={toggleHelp}>{chunks}</TextButton>,
				})}
			</CodeHelp>
			{renderHelpModal()}
		</Wrapper>
	);
};

export default RedeemForm;
